import React, { useState } from 'react';
import { LayoutGrid, Triangle, RotateCcw } from 'lucide-react';
import { VowelHead } from './VowelHead';
import { Trapezoid } from './Trapezoid';
import { useT, useL } from '../../lib/lessonI18n';

const GRID = [['iː', 'ɪ', 'ʊ', 'uː'], ['e', 'ə', 'ɜː', 'ɔː'], ['æ', 'ʌ', 'ɑː', 'ɒ']];
const ROWS = [
  { it: 'Chiuse', en: 'Close' },
  { it: 'Semi-aperte', en: 'Mid' },
  { it: 'Aperte', en: 'Open' }
];
const DIPH_ROW = ['ɪə', 'eɪ', 'ʊə', 'ɔɪ', 'əʊ', 'eə', 'aɪ', 'aʊ'];

const Tab = ({ on, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-all ${on ? 'bg-orange-500 text-slate-950' : 'text-cyan-200 hover:text-white'}`}><Icon size={14} /> {children}</button>
);

// Reference explorer: 3×4 grid or IPA trapezoid, with the sagittal head posed on the selected vowel.
export const RefExplorer = ({ mono, diph, ga, pos, onJump }) => {
  const T = useT();
  const L = useL();
  const [view, setView] = useState('grid');
  const [layer, setLayer] = useState('rp');
  const [sel, setSel] = useState({ ...mono[0], kind: 'mono' });
  const [replay, setReplay] = useState(0);
  const pick = (v, kind) => { setSel({ ...v, kind }); setReplay((r) => r + 1); };
  const byIpa = (ipa, list) => list.find((v) => v.ipa === ipa);
  const isD = sel.kind === 'diph';
  const rounded = (ipa) => !!(byIpa(ipa, mono) || byIpa(ipa, ga) || {}).rounded;
  const pts = [
    ...(layer !== 'ga' ? mono.map((v) => ({ key: v.id, ipa: v.ipa, x: pos[v.ipa][0], y: pos[v.ipa][1], rounded: v.rounded, v, kind: 'mono' })) : []),
    ...(layer !== 'rp' ? ga.map((v) => ({ key: v.id, ipa: v.ipa, x: pos[v.ipa][0], y: pos[v.ipa][1] + (layer === 'both' ? 2.5 : 0), ga: true, v, kind: 'ga' })) : []),
  ];
  const glide = isD ? { from: pos[sel.from], to: pos[sel.to] } : null;
  return (
    <div>
      <div className="mx-auto mb-6 flex w-fit gap-2 rounded-full border border-cyan-500/20 bg-slate-900/60 p-1">
        <Tab on={view === 'grid'} onClick={() => setView('grid')} icon={LayoutGrid} testid="ref-toggle-simple">{T('Griglia semplice', 'Simple grid')}</Tab>
        <Tab on={view === 'trap'} onClick={() => setView('trap')} icon={Triangle} testid="ref-toggle-trapezoid">{T('Trapezoide IPA', 'IPA trapezium')}</Tab>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.25fr_1fr] items-start">
        <div className="min-w-0 rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-4 sm:p-6" data-testid="ref-view">
          {view === 'grid' ? (
            <div data-testid="vowel-simple-grid">
              <div className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-2">
                {GRID.map((row, ri) => (
                  <React.Fragment key={ri}>
                    <span className="self-center pr-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{T(ROWS[ri].it, ROWS[ri].en)}</span>
                    {row.map((ipa) => {
                      const v = byIpa(ipa, mono);
                      const on = sel.id === v.id;
                      return (
                        <button key={ipa} onClick={() => pick(v, 'mono')} data-testid={`grid-vowel-${v.id}`}
                          className={`rounded-xl border px-1 py-3 transition-all hover:-translate-y-0.5 ${on ? 'border-orange-400 bg-orange-500/20' : v.rounded ? 'border-orange-500/20 bg-orange-500/5' : 'border-cyan-500/20 bg-cyan-500/5'}`}>
                          <span className={`block text-2xl font-black ${v.rounded ? 'text-orange-300' : 'text-cyan-200'}`}>{ipa}</span>
                          <span className="block text-[11px] text-slate-400">{v.key}</span>
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-5 mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{T('Dittonghi', 'Diphthongs')}</p>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
                {DIPH_ROW.map((ipa) => {
                  const d = byIpa(ipa, diph);
                  if (!d) return null;
                  return (
                    <button key={ipa} onClick={() => pick(d, 'diph')} data-testid={`grid-diph-${d.id}`}
                      className={`rounded-xl border px-1 py-2.5 transition-all hover:-translate-y-0.5 ${sel.id === d.id ? 'border-orange-400 bg-orange-500/20' : 'border-white/10 bg-slate-950/40'}`}>
                      <span className="block text-lg font-black text-amber-200">{ipa}</span>
                      <span className="block text-[10px] text-slate-400">{d.key}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-3 flex justify-center gap-2">
                {[['rp', T('RP', 'RP')], ['ga', 'General American'], ['both', T('Entrambi', 'Both')]].map(([k, l]) => (
                  <button key={k} onClick={() => setLayer(k)} data-testid={`trap-layer-${k}`} className={`rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${layer === k ? 'bg-cyan-500 text-slate-950' : 'border border-cyan-500/25 text-cyan-200'}`}>{l}</button>
                ))}
              </div>
              <div className="mx-auto aspect-square w-full max-w-md"><Trapezoid points={pts} sel={sel.id} glide={glide} onPick={(p) => pick(p.v, p.kind)} replayKey={replay} gaBelow={layer === 'both'} /></div>
              <p className="mt-2 text-center text-[11px] text-slate-500"><span className="text-cyan-300">●</span> {T('non arrotondata', 'unrounded')} · <span className="text-orange-300">●</span> {T('arrotondata', 'rounded')} · <span className="text-lime-300">●</span> General American</p>
            </div>
          )}
        </div>
        <div className="min-w-0 rounded-3xl border border-orange-400/25 bg-slate-900/60 p-5 lg:sticky lg:top-20" data-testid="ref-head-panel">
          <div className="mx-auto aspect-[17/20] w-full max-w-[260px]">
            <VowelHead pos={pos[isD ? sel.from : sel.ipa]} to={isD ? pos[sel.to] : null} rounded={isD ? rounded(sel.from) : sel.rounded} toRounded={isD ? rounded(sel.to) : false} replayKey={replay} testid="ref-vowel-head" />
          </div>
          <div className="mt-2 flex items-center gap-4">
            <span className={`text-4xl font-black ${sel.kind === 'ga' ? 'text-lime-300' : isD ? 'text-amber-200' : sel.rounded ? 'text-orange-300' : 'text-cyan-200'}`} data-testid="ref-sel-ipa">{sel.ipa}</span>
            <div className="min-w-0">
              <p className="font-bold text-white">{L(sel.key)}</p>
              <p className="text-xs tracking-wide text-slate-400">{L(sel.cls)}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {isD && <button onClick={() => setReplay((r) => r + 1)} data-testid="ref-replay-glide" className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-orange-200"><RotateCcw size={12} /> {T('Rivedi il glide', 'Replay glide')}</button>}
            {sel.kind === 'mono' && <button onClick={() => onJump(sel.id)} data-testid="ref-open-card" className="rounded-full border border-cyan-500/30 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-cyan-200 hover:border-orange-400">{T('Apri la scheda →', 'Open card →')}</button>}
          </div>
        </div>
      </div>
    </div>
  );
};
